import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import {
  DATE_PICKER_FORMAT,
  ISO_FORMAT_INDEX,
  monthBeforeToday,
  today,
  fistDayOfCurrentMonth,
  yearBeforeToday,
  weekBeforeToday,
} from '@app/constants';
import { useMemoizeObject, useUserPrint } from '@app/hooks';
import {
  MONTH,
  WEEK,
  YEAR,
} from '@app/pages/Analytics/OrganizationClientsGrowth/constants/timeRangeNames';
import type {
  ChartDataParams,
  FormValues,
} from '@app/pages/Analytics/OrganizationClientsGrowth/OrganizationClientsGrowth.types';
import { useRegistryFilterState } from '@app/shared/hooks';

import type { RadioChangeEvent } from 'antd';
import type { CheckboxGroupProps } from 'antd/es/checkbox';
import type { BaseOptionType } from 'antd/es/select';

const { useForm } = Form;

export const useOrganizationClientsGrowth = () => {
  const { activeBranchId } = useUserPrint();

  const initialChartDataParams = useMemo(
    () => ({
      DateS: fistDayOfCurrentMonth.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
      DateE: today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
      BranchIds: activeBranchId ? [activeBranchId] : null,
    }),
    [activeBranchId]
  );

  // Выбранные филиалы переживают уход на другой раздел бокового меню и
  // возврат обратно (issue/11161).
  const [selectedBranches, setSelectedBranches] = useRegistryFilterState<
    BaseOptionType[] | null
  >('analytics', 'clients-growth:selectedBranches', null);
  const [chartDataParams, setChartDataParams] = useState<ChartDataParams>(
    initialChartDataParams
  );

  const [form] = useForm();

  const submitForm = ({ Date }: FormValues) => {
    const [start, end] = Date || [];
    const DateS = start || '';
    const DateE = end || '';
    const BranchIds = selectedBranches
      ? selectedBranches.map(({ value }) => Number(value))
      : null;
    const data = {
      DateS,
      DateE,
      BranchIds,
    };

    setChartDataParams(data);
  };

  const radioGroupOptions: CheckboxGroupProps<string>['options'] =
    useMemoizeObject([
      { label: 'Неделя', value: WEEK },
      { label: 'Месяц', value: MONTH },
      { label: 'Год', value: YEAR },
    ]);

  const handleDatePickerValueChange = useCallback(() => {
    form.setFieldValue('RadioGroup', undefined);
  }, [form]);

  const handleRadioGroupValueChange = useCallback(
    (event: RadioChangeEvent) => {
      const { target } = event;
      const { value } = target;

      switch (value) {
        case WEEK:
          form.setFieldValue('Date', [
            weekBeforeToday.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
            today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
          ]);
          break;
        case MONTH:
          form.setFieldValue('Date', [
            monthBeforeToday.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
            today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
          ]);
          break;
        case YEAR:
          form.setFieldValue('Date', [
            yearBeforeToday.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
            today.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX]),
          ]);
          break;
        default:
          break;
      }
    },
    [form]
  );

  const handleReset = useCallback(() => {
    form.resetFields();
    setSelectedBranches([{ value: activeBranchId }]);
    setChartDataParams(initialChartDataParams);
  }, [activeBranchId, form, initialChartDataParams, setSelectedBranches]);

  return {
    selectedBranches,
    setSelectedBranches,
    form,
    submitForm,
    radioGroupOptions,
    handleDatePickerValueChange,
    handleRadioGroupValueChange,
    chartDataParams,
    handleReset,
  };
};
